// src/services/api.js
import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// ─── Abort Controller ─────────────────────────────────────────────────────────
let abortController = new AbortController();

// ─── Axios instance ───────────────────────────────────────────────────────────
const apiClient = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
  xsrfCookieName: 'csrftoken',
  xsrfHeaderName: 'X-CSRFToken',
});

// ─── Request interceptor ──────────────────────────────────────────────────────
apiClient.interceptors.request.use(
  (config) => {
    config.signal = abortController.signal;
    // Always pull the token fresh from storage for every request
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ─── Response interceptor — Robust refresh & redirect prevention ──────────────
let refreshing = false;
let queue = [];

const processQueue = (error, token = null) => {
  queue.forEach((p) => (error ? p.reject(error) : p.resolve(token)));
  queue = [];
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auth/token/refresh/')
    ) {
      if (refreshing) {
        return new Promise((resolve, reject) => {
          queue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return apiClient(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      refreshing = true;

      const refresh = localStorage.getItem('refresh_token');

      // GUARD: If no refresh token is found
      if (!refresh) {
        refreshing = false;
        
        // We only clear storage and redirect if we are NOT on a public page.
        // This prevents the "Flash and Kick" during the registration/login transition.
        const publicPaths = ['/login', '/register'];
        const isPublicPath = publicPaths.some(path => window.location.pathname.includes(path));

        if (!isPublicPath) {
          console.warn("No refresh token found. Redirecting to login.");
          localStorage.clear();
          // Use replace to prevent the user from "going back" to the dashboard
          window.location.replace('/login');
        }
        return Promise.reject(error);
      }

      try {
        // Use a clean axios instance to refresh tokens to avoid interceptor loops
        const { data } = await axios.post(`${BASE_URL}/auth/token/refresh/`, { refresh });
        
        const newAccessToken = data.access;
        localStorage.setItem('access_token', newAccessToken);
        
        // Update the default header for the instance
        apiClient.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`;
        
        processQueue(null, newAccessToken);
        
        // Update the failed request and retry
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        
        // Refresh token failed (likely expired)
        localStorage.clear();
        window.location.replace('/login');
        return Promise.reject(refreshError);
      } finally {
        refreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

// ─── Unwrap helper ────────────────────────────────────────────────────────────
const unwrap = (promise) => promise.then((res) => res.data);

// ═════════════════════════════════════════════════════════════════════════════
//  API SERVICE
// ═════════════════════════════════════════════════════════════════════════════
const apiService = {

  abortAllRequests: () => {
    abortController.abort();
    abortController = new AbortController();
  },

  // Generic methods
  get:    (url, config)       => unwrap(apiClient.get(url, config)),
  post:   (url, data, config) => unwrap(apiClient.post(url, data, config)),
  patch:  (url, data, config) => unwrap(apiClient.patch(url, data, config)),
  put:    (url, data, config) => unwrap(apiClient.put(url, data, config)),
  delete: (url, config)       => unwrap(apiClient.delete(url, config)),

  // CSRF handled via cookies
  fetchCSRFToken: () => Promise.resolve({ data: { detail: 'CSRF handled by cookies' } }),

  // ── Auth ──────────────────────────────────────────────────────────────────
  auth: {
    register:       (data)    => unwrap(apiClient.post('/auth/register/', data)),
    login:          (data)    => unwrap(apiClient.post('/auth/login/', data)),
    logout:         (refresh) => unwrap(apiClient.post('/auth/logout/', { refresh })),
    refreshToken:   (refresh) => unwrap(apiClient.post('/auth/token/refresh/', { refresh })),
    getProfile:     ()        => unwrap(apiClient.get('/auth/profile/')),
    updateProfile:  (data)    => unwrap(apiClient.patch('/auth/profile/update/', data)),
    changePassword: (data)    => unwrap(apiClient.post('/auth/change-password/', data)),
  },

  // ── Problems ──────────────────────────────────────────────────────────────
  problems: {
    list: (params) =>
      apiClient.get('/problems/', { params }).then((res) => {
        const d = res.data;
        if (Array.isArray(d)) return { results: d, count: d.length, next: null, previous: null };
        return { results: d.results || [], count: d.count || 0, next: d.next, previous: d.previous };
      }),
    get:    (id)      => unwrap(apiClient.get(`/problems/${id}/`)),
    create: (data)    => unwrap(apiClient.post('/problems/', data)),
    update: (id, d)   => unwrap(apiClient.patch(`/problems/${id}/`, d)),
    delete: (id)      => unwrap(apiClient.delete(`/problems/${id}/`)),

    getBids:   (id)       => unwrap(apiClient.get(`/problems/${id}/bids/`)),
    submitBid: (id, d)    => unwrap(apiClient.post(`/problems/${id}/bids/`, d)),
    acceptBid: (pid, bid) => unwrap(apiClient.post(`/problems/${pid}/bids/${bid}/accept/`)),

    getTaggedSolutions:  (id, params) => unwrap(apiClient.get(`/problems/${id}/tagged-solutions/`, { params })),
    getBestMatchInsight: (id)         => unwrap(apiClient.get(`/problems/${id}/best-match-insight/`)),
  },

  // ── Solutions ─────────────────────────────────────────────────────────────
  solutions: {
    list: (params) =>
      apiClient.get('/solutions/', { params }).then((res) => {
        const d = res.data;
        if (Array.isArray(d)) return { results: d, count: d.length, next: null, previous: null };
        return { results: d.results || [], count: d.count || 0, next: d.next, previous: d.previous };
      }),
    get:    (id)      => unwrap(apiClient.get(`/solutions/${id}/`)),
    create: (data)    => unwrap(apiClient.post('/solutions/', data)),
    update: (id, d)   => unwrap(apiClient.patch(`/solutions/${id}/`, d)),
    delete: (id)      => unwrap(apiClient.delete(`/solutions/${id}/`)),

    getReviews:  (id)    => unwrap(apiClient.get(`/solutions/${id}/reviews/`)),
    addReview:   (id, d) => unwrap(apiClient.post(`/solutions/${id}/reviews/`, d)),
    requestDemo: (id, d) => unwrap(apiClient.post(`/solutions/${id}/request-demo/`, d)),
    requestPoC:  (id, d) => unwrap(apiClient.post(`/solutions/${id}/request-poc/`, d)),

    getViewerHistory: (id, params) => unwrap(apiClient.get(`/solutions/${id}/viewer-history/`, { params })),
    listCrossSector: (params)      => unwrap(apiClient.get('/solutions/cross-sector/', { params })),
  },

  // ── Organizations ─────────────────────────────────────────────────────────
  organizations: {
    list:       (params) => unwrap(apiClient.get('/organizations/', { params })),
    get:        (id)     => unwrap(apiClient.get(`/organizations/${id}/`)),
    create:     (data)   => unwrap(apiClient.post('/organizations/', data)),
    update:     (id, d)  => unwrap(apiClient.patch(`/organizations/${id}/`, d)),
    delete:     (id)     => unwrap(apiClient.delete(`/organizations/${id}/`)),
    getMembers: (id)     => unwrap(apiClient.get(`/organizations/${id}/members/`)),
  },

  // ── Messaging ─────────────────────────────────────────────────────────────
  messaging: {
    listConversations: ()         => unwrap(apiClient.get('/messaging/')),
    startConversation: (data)     => unwrap(apiClient.post('/messaging/start/', data)),
    getMessages:       (id)       => unwrap(apiClient.get(`/messaging/${id}/`)),
    reply:             (id, body) => unwrap(apiClient.post(`/messaging/${id}/reply/`, { body })),
    getUsage:          ()         => unwrap(apiClient.get('/messaging/usage/')),
  },

  // ── Billing ───────────────────────────────────────────────────────────────
  billing: {
    listPlans:      ()     => unwrap(apiClient.get('/billing/plans/')),
    createCheckout: (data) => unwrap(apiClient.post('/billing/checkout/', data)),
    openPortal:     ()     => unwrap(apiClient.post('/billing/portal/', {})),
    getStatus:      ()     => unwrap(apiClient.get('/billing/status/')),
  },

  // ── Vendor premium ────────────────────────────────────────────────────────
  vendor: {
    getCompetitorInsights:    (pId) => unwrap(apiClient.get(`/problems/${pId}/competitor-insights/`)),
    getSolutionViewerHistory: (sId) => unwrap(apiClient.get(`/solutions/${sId}/viewer-history/`)),
  },

  // ── Subscriptions & Payments ──────────────────────────────────────────────
  subscriptions: {
    list:   ()    => unwrap(apiClient.get('/subscriptions/')),
    get:    (id)  => unwrap(apiClient.get(`/subscriptions/${id}/`)),
    cancel: (id)  => unwrap(apiClient.post(`/subscriptions/${id}/cancel/`)),
  },
  payments: {
    list:    ()    => unwrap(apiClient.get('/payments/')),
    get:     (id)  => unwrap(apiClient.get(`/payments/${id}/`)),
    methods: ()    => unwrap(apiClient.get('/payments/methods/')),
  },
};

export { apiClient };
export default apiService;